import { useNavigate } from "#src/Routers/hooks";
import { RunLogRoutePath } from "#src/routes/data-tools/run-log";
import { Link, ToastProps, useToast } from "@validereinc/common-components";
import { JobStatus, JobType } from "@validereinc/domain";
import React, { useEffect } from "react";

export const getBulkActionToastDetails = ({
  successCount,
  skippedCount,
  failureCount,
  totalCount,
  actionLabel,
}: {
  successCount: number;
  skippedCount: number;
  failureCount: number;
  totalCount: number;
  actionLabel: string;
}): { intent: ToastProps["intent"]; description: string } => {
  const intent = (() => {
    if (successCount && !skippedCount && !failureCount) {
      return "success";
    } else if (failureCount && !successCount && !skippedCount) {
      return "error";
    } else {
      return "info";
    }
  })();

  const description = `${actionLabel.charAt(0).toUpperCase() + actionLabel.slice(1)} results - ${successCount} successful, ${skippedCount} skipped,
 ${failureCount} failed, total: ${totalCount}. `;

  return { intent, description };
};

export const useBulkActionToast = ({
  job,
  resetJob,
  actionLabel,
  showCounts = true,
}: {
  job: JobType;
  resetJob?: () => void;
  actionLabel: string;
  showCounts?: boolean;
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (job?.status === JobStatus.FINISHED) {
      if (job?.processed_items < job?.total_items) {
        toast.push({
          intent: "error",
          description: showCounts
            ? `Processed ${job?.processed_items}/${job?.total_items} row(s)`
            : `Partially processed row(s)`,
        });
      }

      const {
        success: successCount,
        failed: failureCount,
        skipped: skippedCount,
      } = job?.item_status_counts ?? {};
      const totalCount = Object.values(job?.item_status_counts ?? {}).reduce(
        (sum, currentCount) => sum + currentCount,
        0
      );

      const { intent, description } = getBulkActionToastDetails({
        successCount,
        skippedCount,
        failureCount,
        totalCount,
        actionLabel,
      });

      toast.push({
        intent,
        description: (
          <>
            {description}
            <Link
              label="View Details"
              underline="always"
              onClick={() =>
                navigate({
                  pathname: RunLogRoutePath.path,
                })
              }
            />
          </>
        ),
      });

      resetJob?.();
    }

    if (job?.status === JobStatus.FAILED) {
      toast.push({
        intent: "error",
        description: `Failed to ${actionLabel.toLowerCase()}`,
      });
      resetJob?.();
    }
  }, [job?.status]);
};
